import Tooltip, { useAnalysisActiveDot } from './AnalysisTooltip';
import { Checkbox } from './pilot-ui/checkbox';
import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { ChartData, ChartSeries } from '../types';
import NoteMarkerOverlay, { SeriesNotes } from './NoteMarkerOverlay';
import { ChartCopy, ChartUnavailable, explainEmptyAcross } from './chartUnavailable';
import { formatChartDayLabel } from '../utils/calendarDate';
import ChartCsvButton from './ChartCsvButton';
import { CsvColumn } from '../utils/chartCsv';
import { measureRange } from './PressureChart';
import { AXIS_TITLE_STYLE, X_TICK_MARGIN, inH2OTickFormatter, sharedPaDomain } from '../utils/pressureAxis';
import { DATA_LINE_Z_INDEX, TREND_LINE_Z_INDEX } from './referenceLineLayer';
import { buildDeltaRows, hasMeasurements, measuredDeltas } from './transitionDeltaSeries';
import { AverageAlignment, AverageType, fitThirtyDayTrend, movingAverage } from './transitionDeltaMath';
import TransitionDeltaTooltip from './TransitionDeltaTooltip';
import { renderDeltaMarkerLines } from './TransitionDeltaMarkerLines';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './pilot-ui/select';
import { Input } from './pilot-ui/input';
import { Label } from './pilot-ui/label';

interface ReturnDeltasChartProps {
  jsonData: ChartData;
  yDomain?: [number, number];
  overlaySeries?: ChartSeries[];
  noteSeries?: SeriesNotes[];
  onNoteImageClick?: (noteId: number) => void;
}

const COPY: ChartCopy = {
  description:
    'This chart shows the pressure change when the blower switches on and off.',
  measurement: 'transition',
};

// Browser-remembered chart settings; storage may be unavailable in private mode.
const PREF_PREFIX = 'returnDeltasChart.';

const readPreference = (key: string): string | null => {
  try {
    return localStorage.getItem(`${PREF_PREFIX}${key}`);
  } catch {
    return null;
  }
};

const savePreference = (key: string, value: string): void => {
  try {
    localStorage.setItem(`${PREF_PREFIX}${key}`, value);
  } catch {
    // Storage can be unavailable in private mode; the control still works.
  }
};

const readWholeNumber = (key: string, min: number, max: number): number | null => {
  const stored = readPreference(key);
  if (stored === null || !/^\d+$/.test(stored)) return null;
  const value = Number(stored);
  return value >= min && value <= max ? value : null;
};

// The daily values fade behind a moving average drawn in a darker shade of their colour.
const DAILY_OPACITY_UNDER_AVERAGE = 0.2;
const AVERAGE_DARKENING_PERCENT = 50;
const FITTED_LINE_WIDTH = 3;

const averageStroke = (color: string): string =>
  `color-mix(in oklch, ${color}, black ${AVERAGE_DARKENING_PERCENT}%)`;

const AVERAGE_TYPES: AverageType[] = ['simple', 'exponential', 'weighted', 'hull'];
const ALIGNMENTS: AverageAlignment[] = ['trailing', 'centered'];

const ReturnDeltasChart: React.FC<ReturnDeltasChartProps> = ({
  jsonData,
  yDomain: propDomain,
  overlaySeries,
  noteSeries,
  onNoteImageClick,
}) => {
  const activeDot = useAnalysisActiveDot({ r: 8 });
  const controlId = React.useId();
  const [includeZero, setIncludeZero] = React.useState(() => readPreference('includeZero') === '1');
  const toggleIncludeZero = () => {
    const next = !includeZero;
    setIncludeZero(next);
    savePreference('includeZero', next ? '1' : '0');
  };
  const [showAverage, setShowAverage] = React.useState(() => readPreference('movingAverage') === '1');
  const [showTrend, setShowTrend] = React.useState(() => readPreference('trend30') === '1');
  const [averageType, setAverageType] = React.useState<AverageType>(() => {
    const stored = readPreference('averageType');
    return AVERAGE_TYPES.find((type) => type === stored) ?? 'simple';
  });
  const [alignment, setAlignment] = React.useState<AverageAlignment>(() => {
    const stored = readPreference('alignment');
    return ALIGNMENTS.find((value) => value === stored) ?? 'trailing';
  });
  const [windowDays, setWindowDays] = React.useState(() => readWholeNumber('windowDays', 2, 60) ?? 7);
  const [customMinimum, setCustomMinimum] = React.useState<number | null>(() =>
    readWholeNumber('minimumMeasuredDays', 1, readWholeNumber('windowDays', 2, 60) ?? 7));
  const minimumMeasuredDays = customMinimum ?? Math.ceil(windowDays / 2);
  const [windowText, setWindowText] = React.useState(() => String(windowDays));
  const [minimumText, setMinimumText] = React.useState(() => String(minimumMeasuredDays));
  const changeWindow = (value: number) => {
    if (!Number.isInteger(value) || value < 2 || value > 60) return;
    setWindowDays(value);
    savePreference('windowDays', String(value));
    if (customMinimum !== null && customMinimum > value) {
      setCustomMinimum(value);
      savePreference('minimumMeasuredDays', String(value));
      setMinimumText(String(value));
    } else if (customMinimum === null) {
      setMinimumText(String(Math.ceil(value / 2)));
    }
  };
  const changeMinimum = (value: number) => {
    if (!Number.isInteger(value) || value < 1 || value > windowDays) return;
    setCustomMinimum(value);
    savePreference('minimumMeasuredDays', String(value));
  };
  const editWindow = (text: string) => {
    setWindowText(text);
    if (/^\d+$/.test(text)) changeWindow(Number(text));
  };
  const editMinimum = (text: string) => {
    setMinimumText(text);
    if (/^\d+$/.test(text)) changeMinimum(Number(text));
  };
  const isOverlay = !!overlaySeries && overlaySeries.length > 1;
  const series: ChartSeries[] = isOverlay
    ? overlaySeries!
    : [{ id: 'solo', label: 'Pressure Transitions', color: '#2196f3', data: jsonData }];

  // Legacy bail-out, now only for payloads carrying neither values nor a
  // status block — otherwise the panel explains itself below.
  const anyPayload = series.some((s) => s.data?.return_deltas || s.data?.return_deltas_status);
  if (!anyPayload) return null;

  // Build merged date rows; each series contributes a `delta_<id>` column.
  const seriesTransitions = series.map((s) => measuredDeltas(s.data, 'return'));

  // Nothing plottable in ANY series. Say why instead of rendering a titled but
  // empty panel — that reads as broken rather than absent, which is how this
  // reached us as a "missing data" report in the first place. Overlays hit the
  // same wall: two flat devices compared side by side produce exactly the blank
  // panel this exists to remove.
  if (seriesTransitions.every((ts) => !hasMeasurements(ts))) {
    const explanation = explainEmptyAcross(
      series.map((s) => ({ label: s.label, status: s.data?.return_deltas_status })),
      COPY
    );
    if (explanation) {
      return (
        <div className="w-full mt-4">
          <h3 className="text-lg font-semibold mb-4">Return Pressure Transition Deltas</h3>
          <ChartUnavailable {...explanation} />
        </div>
      );
    }
    return null;
  }
  // One row per measured day whether or not the trend is shown; the trend is
  // evaluated at each row's calendar day, so it bends slightly across gaps.
  const chartData = buildDeltaRows(series, 'return');
  const averageLabel = `${windowDays}-day ${averageType} average`;
  const dailyOpacity = showAverage ? DAILY_OPACITY_UNDER_AVERAGE : 1;
  const trends = series.map((item, index) =>
    showTrend ? fitThirtyDayTrend(
      seriesTransitions[index], chartData.map(({ date }) => date), item.data.dates[item.data.dates.length - 1],
    ) : null);
  series.forEach(({ id }, index) => {
    if (showAverage) {
      const values = movingAverage(seriesTransitions[index], {
        type: averageType, window: windowDays, alignment, minimumMeasuredDays,
      }, chartData.map(({ date }) => date));
      chartData.forEach((row, rowIndex) => { row[`average_${id}`] = values[rowIndex]; });
    }
    if (trends[index]) {
      chartData.forEach((row, rowIndex) => { row[`trend_${id}`] = trends[index]?.values[rowIndex] ?? null; });
    }
  });

  // An explicit yDomain prop (shared with the fan-side chart so the two stay
  // comparable) still bounds the data; checking the box widens that window to
  // reach zero rather than discarding it. Deltas are signed, so extend toward
  // zero from whichever side the data sits on. Both axes take this one pair:
  // the inH₂O axis has no series of its own to fit (FS-709).
  const [dataMin, dataMax] = measureRange(chartData, showAverage || showTrend ? /^(delta|average|trend)_/ : /^delta_/);
  const yDomain = sharedPaDomain(
    Math.min(dataMin, propDomain?.[0] ?? Infinity),
    Math.max(dataMax, propDomain?.[1] ?? -Infinity),
    includeZero,
  );

  const csvColumns: CsvColumn<(typeof chartData)[number]>[] = [
    { header: 'Date', value: 'date' },
    ...series.map((s) => ({
      header: isOverlay ? `${s.label} — Pressure delta (Pa)` : 'Pressure delta (Pa)',
      value: `delta_${s.id}`,
    })),
  ];

  return (
    <div className="w-full mt-4">
      <div className="flex items-start justify-between gap-2 mb-8">
        <h3 className="text-lg font-semibold">Return Pressure Transition Deltas</h3>
        <ChartCsvButton chart="return-deltas" rows={chartData} columns={csvColumns} />
      </div>
      <div className="flex items-start gap-4">
        <div className="flex-1">
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={chartData} margin={{ top: 10, right: 50, left: 50, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" yAxisId="left" />
              <XAxis
                dataKey="date"
                tickFormatter={formatChartDayLabel}
                tickMargin={X_TICK_MARGIN}
                label={{ value: 'Date', position: 'insideBottom', offset: -20 }}
              />
              <YAxis
                yAxisId="left"
                domain={yDomain}
                allowDataOverflow
                tickFormatter={(v: number) => Math.round(v).toString()}
                label={{
                  value: 'Pressure Delta (Pa)',
                  angle: -90,
                  position: 'insideLeft',
                  style: AXIS_TITLE_STYLE,
                  offset: -35,
                }}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                domain={yDomain}
                allowDataOverflow
                tick={{ fontSize: 12 }}
                tickFormatter={inH2OTickFormatter(yDomain)}
                label={{
                  value: 'Pressure Delta (inH₂O)',
                  angle: 90,
                  position: 'insideRight',
                  style: AXIS_TITLE_STYLE,
                  offset: -35,
                }}
              />
              <Tooltip content={<TransitionDeltaTooltip series={series} isOverlay={isOverlay} averageLabel={showAverage ? averageLabel : undefined} />} />
              {isOverlay && <Legend verticalAlign="top" height={20} itemSorter={null} />}
              {series.map((s) => (
                <Line
                  key={`delta_${s.id}`}
                  zIndex={DATA_LINE_Z_INDEX}
                  type="monotone"
                  dataKey={`delta_${s.id}`}
                  name={s.label}
                  yAxisId="left"
                  stroke={s.color}
                  strokeOpacity={dailyOpacity}
                  dot={{ fill: s.color, fillOpacity: dailyOpacity }}
                  activeDot={activeDot}
                  connectNulls={false}
                />
              ))}
              {renderDeltaMarkerLines({ series, rows: chartData })}
              {/* The average and trend sit above the data layer, as the pressure chart's trend does. */}
              {showAverage && series.map((s) => (
                <Line
                  key={`average_${s.id}`}
                  zIndex={TREND_LINE_Z_INDEX}
                  type="linear"
                  dataKey={`average_${s.id}`}
                  name={`${s.label} — ${averageLabel}`}
                  yAxisId="left"
                  stroke={averageStroke(s.color)}
                  strokeDasharray="5 3"
                  strokeWidth={FITTED_LINE_WIDTH}
                  dot={false}
                  activeDot={false}
                  legendType="none"
                  connectNulls={false}
                  isAnimationActive={false}
                />
              ))}
              {showTrend && series.filter((_s, index) => trends[index] !== null).map((s) => (
                <Line
                  key={`trend_${s.id}`}
                  zIndex={TREND_LINE_Z_INDEX}
                  type="linear"
                  dataKey={`trend_${s.id}`}
                  name={`${s.label} — 30-day trend`}
                  yAxisId="left"
                  stroke={isOverlay ? s.color : '#dc2626'}
                  strokeDasharray="5 5"
                  strokeWidth={FITTED_LINE_WIDTH}
                  dot={false}
                  activeDot={false}
                  legendType="none"
                  connectNulls={false}
                  isAnimationActive={false}
                />
              ))}
              {noteSeries && noteSeries.length > 0 && onNoteImageClick && (
                <NoteMarkerOverlay seriesNotes={noteSeries} onImageClick={onNoteImageClick} />
              )}
            </LineChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap items-center gap-4 mt-1 text-xs text-muted-foreground">
            <label className="flex items-center gap-1.5 select-none cursor-pointer w-fit">
              <Checkbox
                id={`${controlId}-include-zero`}
                className="size-6"
                checked={includeZero}
                onCheckedChange={toggleIncludeZero}
                title="Include zero on the pressure axis"
              />
              Include zero on the pressure axis
            </label>
            <label className="flex items-center gap-1.5 select-none cursor-pointer w-fit">
              <Checkbox
                id={`${controlId}-average`}
                className="size-6"
                checked={showAverage}
                onCheckedChange={() => {
                  setShowAverage(!showAverage);
                  savePreference('movingAverage', showAverage ? '0' : '1');
                }}
                title="Show a moving average of measured pressure drops"
              />
              Moving average
            </label>
            <label className="flex flex-wrap items-center gap-1.5 select-none cursor-pointer w-fit">
              <Checkbox
                id={`${controlId}-trend`}
                className="size-6"
                checked={showTrend}
                onCheckedChange={() => {
                  setShowTrend(!showTrend);
                  savePreference('trend30', showTrend ? '0' : '1');
                }}
                title="Show the fitted pressure-drop trend over the last 30 calendar days"
              />
              30-day trend
              {showTrend && series.map((s, index) => (
                <span key={s.id}>
                  {isOverlay ? `${s.label}: ` : ''}{trends[index]
                    ? `${trends[index].slope >= 0 ? '+' : ''}${trends[index].slope.toFixed(2)} Pa/day`
                    : 'not enough days'}
                </span>
              ))}
            </label>
          </div>
          {showAverage && (
            <div className="flex flex-wrap items-end gap-3 mt-2 text-xs text-muted-foreground">
              <div className="flex flex-col gap-1">
                <Label htmlFor={`${controlId}-type`}>Type</Label>
                <Select value={averageType} onValueChange={(value) => {
                  const chosen = AVERAGE_TYPES.find((type) => type === value);
                  if (!chosen) return;
                  setAverageType(chosen);
                  savePreference('averageType', chosen);
                }}>
                  <SelectTrigger id={`${controlId}-type`} aria-describedby={`${controlId}-type-help`} title="Choose how the moving average weights measured days"><SelectValue>{averageType[0].toUpperCase() + averageType.slice(1)}</SelectValue></SelectTrigger>
                  <SelectContent listLabel="Moving average types">
                    <SelectItem value="simple">Simple</SelectItem>
                    <SelectItem value="exponential">Exponential</SelectItem>
                    <SelectItem value="weighted">Weighted</SelectItem>
                    <SelectItem value="hull">Hull</SelectItem>
                  </SelectContent>
                </Select>
                <p id={`${controlId}-type-help`}>How measured days are weighted.</p>
              </div>
              <div className="flex flex-col gap-1">
                <Label htmlFor={`${controlId}-window`}>Window (days)</Label>
                <Input id={`${controlId}-window`} aria-describedby={`${controlId}-window-help`} className="w-24" type="number" min={2} max={60} step={1} value={windowText} onChange={(event) => editWindow(event.target.value)} onBlur={() => setWindowText(String(windowDays))} />
                <p id={`${controlId}-window-help`}>2 to 60 calendar days.</p>
              </div>
              <div className="flex flex-col gap-1">
                <Label htmlFor={`${controlId}-alignment`}>Alignment</Label>
                <Select value={alignment} onValueChange={(value) => {
                  const chosen = ALIGNMENTS.find((mode) => mode === value);
                  if (!chosen) return;
                  setAlignment(chosen);
                  savePreference('alignment', chosen);
                }}>
                  <SelectTrigger id={`${controlId}-alignment`} aria-describedby={`${controlId}-alignment-help`} title="Choose whether the average looks backward or around each day"><SelectValue>{alignment[0].toUpperCase() + alignment.slice(1)}</SelectValue></SelectTrigger>
                  <SelectContent listLabel="Moving average alignments">
                    <SelectItem value="trailing">Trailing</SelectItem>
                    <SelectItem value="centered">Centered</SelectItem>
                  </SelectContent>
                </Select>
                <p id={`${controlId}-alignment-help`}>Past days or days around each date.</p>
              </div>
              <div className="flex flex-col gap-1">
                <Label htmlFor={`${controlId}-minimum`}>Minimum measured days</Label>
                <Input id={`${controlId}-minimum`} aria-describedby={`${controlId}-minimum-help`} className="w-24" type="number" min={1} max={windowDays} step={1} value={minimumText} onChange={(event) => editMinimum(event.target.value)} onBlur={() => setMinimumText(String(minimumMeasuredDays))} />
                <p id={`${controlId}-minimum-help`}>Skip windows with fewer readings.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReturnDeltasChart;
